import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { useRegistrationStore } from '@/lib/store/registrationStore';
import { cn } from '@/lib/utils';
import { Check } from 'lucide-react';
import { usePageTranslation } from '@/i18n/useTranslation';
import { authTranslations } from '@/i18n/locales/en/auth';
import { authTranslations as thAuthTranslations } from '@/i18n/locales/th/auth';
import {
  OwnWebsiteIcon, ShopeeIcon, LazadaIcon, TikTokIcon,
  LineIcon, FacebookIcon, InstagramIcon, OfflineStoreIcon,
} from '@/components/icons/BrandIcons';

interface StepSellingChannelsProps {
  onNext: () => void;
}

type SellingChannelLabelKey =
  | 'sellingChOwnWebsite' | 'sellingChShopee' | 'sellingChLazada' | 'sellingChTikTok'
  | 'sellingChLine' | 'sellingChFacebook' | 'sellingChInstagram' | 'sellingChOffline';

const CHANNEL_OPTIONS: { id: string; icon: ReactNode; labelKey: SellingChannelLabelKey }[] = [
  { id: 'own_website', icon: <OwnWebsiteIcon className="w-6 h-6" />, labelKey: 'sellingChOwnWebsite' },
  { id: 'shopee', icon: <ShopeeIcon className="w-6 h-6" />, labelKey: 'sellingChShopee' },
  { id: 'lazada', icon: <LazadaIcon className="w-6 h-6" />, labelKey: 'sellingChLazada' },
  { id: 'tiktok', icon: <TikTokIcon className="w-6 h-6" />, labelKey: 'sellingChTikTok' },
  { id: 'line', icon: <LineIcon className="w-6 h-6" />, labelKey: 'sellingChLine' },
  { id: 'facebook', icon: <FacebookIcon className="w-6 h-6" />, labelKey: 'sellingChFacebook' },
  { id: 'instagram', icon: <InstagramIcon className="w-6 h-6" />, labelKey: 'sellingChInstagram' },
  { id: 'offline', icon: <OfflineStoreIcon className="w-6 h-6" />, labelKey: 'sellingChOffline' },
];

export function StepSellingChannels({ onNext }: StepSellingChannelsProps) {
  const { qualificationData, updateQualificationData } = useRegistrationStore();
  const t = usePageTranslation({ en: authTranslations, th: thAuthTranslations });

  const selected = qualificationData.sellingChannels || [];

  const handleToggle = (id: string) => {
    const next = selected.includes(id)
      ? selected.filter((c) => c !== id)
      : [...selected, id];
    updateQualificationData({ sellingChannels: next });
  };

  return (
    <div className="flex flex-col">
      {/* Title */}
      <div className="mb-4">
        <h2 className="text-xl font-semibold text-foreground md:text-center">
          {t.sellingChannelsTitle}
        </h2>
      </div>

      {/* Scrollable options */}
      <div className="space-y-2.5 mb-4">
        {CHANNEL_OPTIONS.map((opt) => {
          const isSelected = selected.includes(opt.id);
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => handleToggle(opt.id)}
              className={cn(
                'w-full flex items-center gap-3 px-4 py-3 rounded-md border transition-all',
                isSelected
                  ? 'border-primary bg-primary/10'
                  : 'border-border hover:bg-muted'
              )}
            >
              <span className="shrink-0">{opt.icon}</span>
              <span className="flex-1 text-left font-medium text-sm text-foreground">
                {t[opt.labelKey]}
              </span>
              <div
                className={cn(
                  'w-5 h-5 rounded-sm border-2 flex items-center justify-center shrink-0 transition-colors',
                  isSelected ? 'border-primary bg-primary' : 'border-muted-foreground/30'
                )}
              >
                {isSelected && <Check className="w-3.5 h-3.5 text-white" />}
              </div>
            </button>
          );
        })}
      </div>

      {/* Sticky CTA */}
      <div className="sticky bottom-0 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] bg-gradient-to-t from-background via-background to-transparent">
        <Button onClick={onNext} className="w-full h-12 rounded-md text-base" disabled={selected.length === 0}>
          {t.next}
        </Button>
      </div>
    </div>
  );
}
